<template>
  <div
    v-loading="loading"
    class="data-ops-page"
    :class="{ 'data-ops-page--dashboard-scroll': !loading && current }"
  >
    <p v-if="!loading && error" class="err">{{ error }}</p>
    <p v-else-if="!loading && !current" class="empty-import">
      尚未成功导入
      <router-link :to="{ name: 'data-source' }">去数据源同步</router-link>
    </p>
    <template v-else-if="!loading && current">
      <div class="dashboard">
        <KpiBoard :items="kpiItems" :columns="5" />
        <ChartsPanel title="图表" class="dashboard__charts" @change="onChartPanelChange">
          <ChartCard title="各车间占比" full>
            <EChart ref="deptChart" :option="deptChartOption" height="220px" />
          </ChartCard>
        </ChartsPanel>
      </div>
      <Crud
        class="list-page-crud"
        :filter-items="rowFilterItems"
        :value="rowFilters"
        filters-collapsible
        :pagination="rowPagination"
        @input="onRowFilterInput"
        @filters-panel-change="onFiltersPanelChange"
      >
        <template #filters-actions>
          <el-button-group>
            <el-button
              size="small"
              :type="columnMode === 'follow' ? 'primary' : 'default'"
              @click="setColumnMode('follow')"
            >
              跟进列
            </el-button>
            <el-button
              size="small"
              :type="columnMode === 'full' ? 'primary' : 'default'"
              @click="setColumnMode('full')"
            >
              完整列
            </el-button>
          </el-button-group>
          <el-button size="small" plain icon="el-icon-refresh-left" @click="onResetRowFilters">
            重置筛选
          </el-button>
        </template>
        <template #table>
          <Table
            :key="columnMode"
            ref="complaintTable"
            :columns="complaintTableColumns"
            :data="pagedRows"
            :loading="loading"
            row-key="sourceRow"
            border
            height="100%"
            :on-row-click="openRow"
          />
        </template>
      </Crud>
      <ComplaintDetailDrawer
        :visible="drawerOpen"
        :row="activeRow"
        :person-options="rowFzrOptions"
        @close="drawerOpen = false"
      />
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import { ComplaintRow, ComplaintSummary, CurrentImport, Rate, fetchCurrent } from '@/api/import';
import { closureStatusOf, overdueDays, replyOntimeRate } from '@/domain/complaint-closure';
import { SIM_TODAY } from '@/utils/time';
import { ChartCard, ChartsPanel, EChart, KpiBoard } from '@/components';
import type { EChartOption } from '@/components/echart/echart.vue';
import type { KpiItem } from '@/components/kpi-board/kpi-board.vue';
import { Crud, Table } from '@/components/crud';
import type { FilterItem } from '@/components/crud/types';
import type { ColumnSchema } from '@/components/crud/types';
import {
  type ComplaintRowFilterState,
  buildComplaintRowFilterItems,
  emptyComplaintRowFilters,
  filterComplaintRows,
} from '@/domain/complaint-row-filters';
import { summarizeComplaintRows } from '@/domain/complaint-summary';
import { complaintFollowColumns, complaintRowColumns } from './complaint-columns';
import { ComplaintDetailDrawer } from '@/components';

const DEFAULT_PAGE_SIZE = 50;

export default Vue.extend({
  name: 'CurrentBatch',
  components: { ChartCard, ChartsPanel, ComplaintDetailDrawer, Crud, EChart, KpiBoard, Table },
  data() {
    return {
      loading: true,
      error: '' as string,
      current: null as CurrentImport | null,
      rowPage: 1,
      rowPageSize: DEFAULT_PAGE_SIZE,
      rowFilters: emptyComplaintRowFilters() as ComplaintRowFilterState,
      columnMode: 'follow' as 'follow' | 'full',
      drawerOpen: false,
      activeRow: null as ComplaintRow | null,
    };
  },
  computed: {
    allRows(): ComplaintRow[] {
      return this.current?.rows || [];
    },
    rowFzrOptions(): string[] {
      const seen = new Set<string>();
      const list: string[] = [];
      this.allRows.forEach((row) => {
        const name = (row.fzr || '').trim();
        if (!name || seen.has(name)) return;
        seen.add(name);
        list.push(name);
      });
      return list.sort((a, b) => a.localeCompare(b, 'zh-CN'));
    },
    rowFilterItems(): FilterItem[] {
      return buildComplaintRowFilterItems(this.allRows);
    },
    summaryRows(): ComplaintRow[] {
      return filterComplaintRows(this.allRows, { ...this.rowFilters, closure: '' });
    },
    filteredRows(): ComplaintRow[] {
      return filterComplaintRows(this.allRows, this.rowFilters);
    },
    filteredSummary(): ComplaintSummary {
      return summarizeComplaintRows(this.summaryRows);
    },
    replyOntime(): Rate {
      return replyOntimeRate(this.summaryRows);
    },
    kpiItems(): KpiItem[] {
      const summary = this.filteredSummary;
      return [
        {
          value: summary.total,
          label: '异常批次总数',
          icon: 'el-icon-s-data',
          tone: 'primary',
        },
        {
          value: this.formatRateValue(summary.defect),
          label: this.formatRateLabel('不良品处理', summary.defect),
          icon: 'el-icon-circle-check',
          tone: 'success',
        },
        {
          value: this.formatRateValue(summary.reply),
          label: this.formatRateLabel('原因对策回复', summary.reply),
          icon: 'el-icon-message',
          tone: 'info',
        },
        {
          value: this.formatRateValue(summary.close),
          label: this.formatRateLabel('结案', summary.close),
          icon: 'el-icon-finished',
          tone: 'warning',
        },
        {
          value: this.formatRateValue(this.replyOntime),
          label: this.formatRateLabel('异常回复及时', this.replyOntime),
          icon: 'el-icon-time',
          tone: 'success',
        },
      ];
    },
    deptChartOption(): EChartOption {
      const departments = this.filteredSummary.departments;
      if (!departments.length) return {};
      return {
        color: ['#3b6fe0', '#5b8def', '#4a90e2', '#22a06b', '#d9822b', '#7b68ee', '#7a8494'],
        tooltip: {
          trigger: 'item',
          formatter: '{b}<br/>{c} 批 ({d}%)',
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 8,
          top: 'middle',
          textStyle: { color: '#566070', fontSize: 12 },
        },
        series: [
          {
            type: 'pie',
            radius: ['36%', '58%'],
            center: ['36%', '50%'],
            top: 4,
            bottom: 4,
            avoidLabelOverlap: true,
            minShowLabelAngle: 4,
            label: {
              show: true,
              position: 'outside',
              formatter: '{b} {d}%',
              fontSize: 11,
              color: '#566070',
            },
            labelLine: {
              show: true,
              length: 10,
              length2: 8,
              smooth: 0.15,
              lineStyle: { color: '#c5cdd8' },
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 12,
                fontWeight: 'bold',
              },
            },
            data: departments.map((dept) => ({
              name: dept.name,
              value: dept.count,
            })),
          },
        ],
      };
    },
    complaintTableColumns(): ColumnSchema[] {
      const startIndex = (this.rowPage - 1) * this.rowPageSize + 1;
      const source = this.columnMode === 'follow' ? complaintFollowColumns : complaintRowColumns;
      return source.map((col) => (col.type === 'index' ? { ...col, startIndex } : col));
    },
    pagedRows(): Array<ComplaintRow & { closureStatus: string; followStatus: string; overdueDays: number | null }> {
      const start = (this.rowPage - 1) * this.rowPageSize;
      return this.filteredRows.slice(start, start + this.rowPageSize).map((row) => {
        const status = closureStatusOf(row, SIM_TODAY);
        return {
          ...row,
          closureStatus: status,
          followStatus: status,
          overdueDays: overdueDays(row, SIM_TODAY),
        };
      });
    },
    rowPagination(): {
      page: number;
      pageSize: number;
      total: number;
      layout: string;
      pageSizes: number[];
      onPageChange: (page: number) => void;
      onSizeChange: (size: number) => void;
    } {
      return {
        page: this.rowPage,
        pageSize: this.rowPageSize,
        total: this.filteredRows.length,
        layout: 'total, sizes, prev, pager, next, jumper',
        pageSizes: [10, 20, 50, 100],
        onPageChange: (page: number) => {
          this.rowPage = page;
        },
        onSizeChange: (size: number) => {
          this.rowPageSize = size;
          this.rowPage = 1;
        },
      };
    },
  },
  watch: {
    allRows() {
      this.rowFilters = emptyComplaintRowFilters();
      this.rowPage = 1;
    },
  },
  created() {
    this.loadCurrent();
  },
  methods: {
    loadCurrent() {
      this.loading = true;
      return fetchCurrent()
        .then((payload) => {
          this.current = payload.current;
          this.error = '';
        })
        .catch((err: Error) => {
          this.error = err.message || '请求失败';
        })
        .then(() => {
          this.loading = false;
        });
    },
    onRowFilterInput(value: ComplaintRowFilterState) {
      this.rowFilters = value;
      this.rowPage = 1;
    },
    onResetRowFilters() {
      this.rowFilters = emptyComplaintRowFilters();
      this.rowPage = 1;
    },
    onFiltersPanelChange() {
      this.$nextTick(() => {
        const table = this.$refs.complaintTable as { $refs?: { elTable?: { doLayout?: () => void } } } | undefined;
        table?.$refs?.elTable?.doLayout?.();
      });
    },
    formatRateLabel(name: string, rate: Rate): string {
      return `${name} ${rate.filled}/${rate.total}`;
    },
    formatRateValue(rate: Rate): string {
      return rate.percent === null ? '无' : rate.percent.toFixed(1) + '%';
    },
    setColumnMode(mode: 'follow' | 'full') {
      this.columnMode = mode;
      this.onFiltersPanelChange();
    },
    openRow(row: ComplaintRow) {
      this.activeRow = row;
      this.drawerOpen = true;
    },
    onChartPanelChange(open: boolean) {
      if (!open) return;
      this.$nextTick(() => {
        const chart = this.$refs.deptChart as { resizeChart?: () => void } | undefined;
        chart?.resizeChart?.();
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.data-ops-page {
  @include list-page-layout;
  position: relative;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: $color-text;

  &--dashboard-scroll {
    flex: 0 0 auto;
    height: auto;
    min-height: 0;
  }
}

.err {
  color: $color-danger;
  font-size: 14px;
}

.empty-import {
  font-size: 14px;
  color: $color-text-secondary;

  a {
    margin-left: 8px;
    color: $color-primary;
  }
}

.dashboard {
  flex-shrink: 0;

  &__charts {
    margin-top: 12px;
  }
}

.list-page-crud ::v-deep .el-table__row {
  cursor: pointer;
}
</style>
